import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useParticipants, useTracks, VideoTrack, useLocalParticipant } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useRoom } from './RoomProvider';

interface ParticipantStripProps {
  onSelectFeatured: (identity: string | null) => void;
  pinnedParticipant?: string | null;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  bottom: number;
  participantIdentity: string | null;
  participantName: string | null;
  isLocal: boolean;
}

export function ParticipantStrip({ onSelectFeatured, pinnedParticipant }: ParticipantStripProps) {
  const participants = useParticipants();
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone], { onlySubscribed: false });
  const {
    modelParticipantIds,
    isHost: viewerIsHost,
    hostParticipantIds,
    originalHostId,
    socket,
    participants: sessionParticipants,
    stagePinnedParticipantId,
    pinStageParticipant,
    clearStagePinnedParticipant,
    banParticipant,
  } = useRoom();
  const { localParticipant } = useLocalParticipant();
  const sessionParticipantById = useMemo(() => (
    new Map(sessionParticipants.map(participant => [participant.participantId, participant]))
  ), [sessionParticipants]);

  // Check if current user can ban (is host or model)
  const isCurrentUserModel = !!localParticipant?.identity && modelParticipantIds.includes(localParticipant.identity);
  const canBan = viewerIsHost || isCurrentUserModel;
  const canModerateParticipantAudio = viewerIsHost || isCurrentUserModel;

  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    bottom: 0,
    participantIdentity: null,
    participantName: null,
    isLocal: false,
  });
  const [contextMenuPosition, setContextMenuPosition] = useState({ left: 0, top: 0 });
  const contextMenuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!contextMenu.visible || !contextMenuRef.current) return;

    const margin = 8;
    const gap = 8;
    const menu = contextMenuRef.current;
    const menuWidth = menu.offsetWidth;
    const menuHeight = menu.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const clampedLeft = Math.min(
      Math.max(contextMenu.x - menuWidth / 2, margin),
      Math.max(margin, viewportWidth - menuWidth - margin)
    );
    const preferredTop = contextMenu.y - menuHeight - gap;
    const fallbackTop = contextMenu.bottom + gap;
    const unclampedTop = preferredTop >= margin ? preferredTop : fallbackTop;
    const clampedTop = Math.min(
      Math.max(unclampedTop, margin),
      Math.max(margin, viewportHeight - menuHeight - margin)
    );

    setContextMenuPosition({ left: clampedLeft, top: clampedTop });
  }, [contextMenu]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Handle click on participant thumbnail (both left and right click)
  const handleThumbnailClick = (e: React.MouseEvent, identity: string, name: string, thumbnailEl: HTMLDivElement) => {
    e.preventDefault();
    e.stopPropagation();

    const isLocal = identity === localParticipant?.identity;
    const rect = thumbnailEl.getBoundingClientRect();

    setContextMenu({
      visible: true,
      x: rect.left + rect.width / 2, // Center horizontally
      y: rect.top, // Position above thumbnail
      bottom: rect.bottom,
      participantIdentity: identity,
      participantName: name,
      isLocal,
    });
  };

  // Hosts/models mute the target's microphone at the room level via LiveKit.
  const handleToggleMute = () => {
    if (!contextMenu.participantIdentity) return;

    const identity = contextMenu.participantIdentity;
    const participant = participants.find(p => p.identity === identity);

    if (!participant || !canModerateParticipantAudio) {
      setContextMenu(prev => ({ ...prev, visible: false }));
      return;
    }

    if (!socket) {
      alert('Unable to update participant microphone right now');
      setContextMenu(prev => ({ ...prev, visible: false }));
      return;
    }

    socket.emit('room:mute-participant', identity, (result) => {
      if (!result.success) {
        alert(result.error || 'Failed to update participant microphone');
      }
    });
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const handleTogglePin = () => {
    if (!contextMenu.participantIdentity) return;

    const identity = contextMenu.participantIdentity;
    const isPinnedLocally = pinnedParticipant === identity;
    onSelectFeatured(isPinnedLocally ? null : identity);
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const handleToggleSpotlight = () => {
    if (!contextMenu.participantIdentity) return;

    const identity = contextMenu.participantIdentity;
    const isSpotlightedByOrganizer = stagePinnedParticipantId === identity;
    if (isSpotlightedByOrganizer) {
      clearStagePinnedParticipant();
    } else {
      pinStageParticipant(identity);
    }
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  // Remove a participant and block their account from this event.
  // The optional note is stored privately for the moderation record.
  const handleBan = () => {
    if (!contextMenu.participantIdentity) return;

    const identity = contextMenu.participantIdentity;
    const participantName = contextMenu.participantName || identity;
    const reason = prompt(
      `Remove ${participantName} and block them from this event? They cannot re-enter unless you allow them back in.\n\nOptional private note for the moderation record (never shown to them):`
    );
    if (reason === null) {
      setContextMenu(prev => ({ ...prev, visible: false }));
      return;
    }

    banParticipant(identity, reason.trim() || null, (result) => {
      if (!result.success) {
        alert(result.error || 'Failed to remove participant');
      }
    });

    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  // Check if a participant can be banned (not the organizer, not self)
  const canBanParticipant = (identity: string) => {
    return canBan && identity !== originalHostId && identity !== localParticipant?.identity;
  };

  const admittedParticipants = participants;

  // Categorize participants - same for everyone viewing
  const modelIdSet = new Set([
    ...modelParticipantIds,
    ...sessionParticipants
      .filter(participant => participant.role === 'model')
      .map(participant => participant.participantId),
  ]);
  const models = admittedParticipants.filter(p => modelIdSet.has(p.identity));
  const artists = admittedParticipants.filter(p => !modelIdSet.has(p.identity));

  // Get track for a participant
  const getCameraTrackForParticipant = (identity: string) => {
    return tracks.find(t => t.participant.identity === identity && t.source === Track.Source.Camera);
  };

  const isParticipantMicrophoneMuted = (participant: typeof participants[0]) => {
    const microphonePublication = participant.getTrackPublication(Track.Source.Microphone);
    return participant.isMicrophoneEnabled === false || !microphonePublication || microphonePublication.isMuted;
  };

  const contextParticipant = contextMenu.participantIdentity
    ? participants.find(p => p.identity === contextMenu.participantIdentity)
    : null;
  const isContextParticipantMuted = contextParticipant
    ? isParticipantMicrophoneMuted(contextParticipant)
    : false;

  // Check if participant is the local user
  const isLocalUser = (identity: string) => localParticipant?.identity === identity;

  const getDisplayName = (participant: typeof participants[0]) => {
    const sessionDisplayName = sessionParticipantById.get(participant.identity)?.displayName?.trim();
    return sessionDisplayName || participant.name || participant.identity.slice(0, 6);
  };

  // Render a participant thumbnail
  const renderThumbnail = (participant: typeof participants[0]) => {
    const track = getCameraTrackForParticipant(participant.identity);
    const isLocal = isLocalUser(participant.identity);
    const displayName = getDisplayName(participant);
    const isMicrophoneMuted = isParticipantMicrophoneMuted(participant);
    const isOrganizerPinned = stagePinnedParticipantId === participant.identity;
    const isLocalPinned = pinnedParticipant === participant.identity;
    const isPinned = isLocalPinned || (!pinnedParticipant && isOrganizerPinned);
    const isOrganizer = hostParticipantIds.includes(participant.identity);
    const isModel = modelIdSet.has(participant.identity);

    return (
      <div
        key={participant.identity}
        data-testid="participant-thumbnail"
        onClick={(e) => handleThumbnailClick(e, participant.identity, displayName, e.currentTarget)}
        onContextMenu={(e) => handleThumbnailClick(e, participant.identity, displayName, e.currentTarget)}
        className={`w-[102px] h-full min-h-[75px] bg-[#0a0807] ${isPinned ? 'ring-2 ring-accent/60 ring-inset' : ''} flex-shrink-0 relative overflow-hidden cursor-pointer transition-colors shadow-sm`}
      >
        {track?.publication?.track ? (
          <VideoTrack
            trackRef={track}
            className="absolute inset-0 w-full h-full object-cover"
            style={isLocal ? { transform: 'scaleX(-1)' } : undefined}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#2a1e15] to-[#0c0a09]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <span className="absolute bottom-1 left-1.5 text-[10px] text-white font-medium drop-shadow-lg">
          {isLocal ? 'You' : displayName}
        </span>
        {(isOrganizer || isModel) && (
          <div className="absolute top-1 left-1.5 flex flex-col items-start gap-0.5">
            {isOrganizer && (
              <span className="px-1.5 py-0.5 rounded bg-accent/90 text-[8px] font-semibold uppercase text-white shadow-sm">
                Host
              </span>
            )}
            {isModel && (
              <span className="px-1.5 py-0.5 rounded bg-tag/90 text-[8px] font-semibold uppercase text-white shadow-sm">
                Model
              </span>
            )}
          </div>
        )}
        {/* Pinned indicator */}
        {isPinned && (
          <span className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center bg-accent rounded text-white">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
            </svg>
          </span>
        )}
        {/* Muted indicator */}
        {isMicrophoneMuted && (
          <span className={`absolute ${isPinned ? 'top-6' : 'top-1'} right-1 w-4 h-4 flex items-center justify-center bg-warn/80 rounded text-white`}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex items-stretch bg-transparent w-full h-full">
        <div className="flex items-stretch overflow-x-auto overflow-y-hidden w-full h-full">
          <div className="flex items-stretch min-w-max h-full divide-x divide-white">
            {models.length === 0 ? (
              <div className="w-[102px] h-full min-h-[75px] bg-[#0a0807] flex-shrink-0 flex items-center justify-center">
                <span className="text-[10px] text-text-faint">No model</span>
              </div>
            ) : (
              models.map((model) => renderThumbnail(model))
            )}

            {artists.map((artist) => renderThumbnail(artist))}
          </div>
        </div>
      </div>

      {/* Context menu - positioned above thumbnail */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="fixed z-50 bg-panel border border-line rounded-lg shadow-xl py-1 min-w-[140px]"
          style={{
            left: contextMenuPosition.left,
            top: contextMenuPosition.top,
          }}
        >
          <div className="px-3 py-1.5 text-xs text-text-dim border-b border-line truncate">
            {contextMenu.isLocal ? 'You' : contextMenu.participantName}
          </div>

          {/* Personal pin option */}
          <button
            onClick={handleTogglePin}
            className="w-full px-3 py-2 text-left text-sm hover:bg-panel2 transition-colors flex items-center gap-2"
          >
            {pinnedParticipant === contextMenu.participantIdentity ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                <span>Unpin</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
                <span>Pin</span>
              </>
            )}
          </button>

          {/* Organizer spotlight option */}
          {viewerIsHost && (
            <button
              onClick={handleToggleSpotlight}
              className="w-full px-3 py-2 text-left text-sm hover:bg-panel2 transition-colors flex items-center gap-2"
            >
              {stagePinnedParticipantId === contextMenu.participantIdentity ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                  <span>Clear spotlight</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2 6.4 20.2 7.5 14 3 9.6l6.2-.9L12 3z" />
                  </svg>
                  <span>Spotlight</span>
                </>
              )}
            </button>
          )}

          {/* Mute option - only for non-local participants */}
          {!contextMenu.isLocal && canModerateParticipantAudio && contextParticipant && !isContextParticipantMuted && (
            <button
              onClick={handleToggleMute}
              className="w-full px-3 py-2 text-left text-sm hover:bg-panel2 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4 text-warn" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path d="M11 5L6 9H2v6h4l5 4V5z" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
              <span>Mute microphone</span>
            </button>
          )}

          {/* Ban option - only for hosts/models, and not for the organizer or self */}
          {canBanParticipant(contextMenu.participantIdentity || '') && (
            <>
              <div className="border-t border-line my-1" />
              <button
                onClick={handleBan}
                className="w-full px-3 py-2 text-left text-sm hover:bg-warn/10 transition-colors flex items-center gap-2 text-warn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Remove and block from this event</span>
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
